// Local copy of the training being edited, kept in IndexedDB (key = trainingId)
// so a lost/expired session or a failed save never costs the user their work.
// Raw indexedDB, no deps. Every function resolves (never rejects): a broken
// IndexedDB must not break saving. Editor-only -- the visor never imports this.
import { getBaseUpdatedAt } from "./saveConflict.es6";

const DB_NAME = "aidea_studio_drafts";
const STORE = "drafts";
export const DRAFT_MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;
export const LOCAL_DRAFT_NOTICE_EVENT = "aidea:local-draft-notice";
// Pre-IndexedDB snapshot written by redirectToAuth; only read to migrate.
export const LEGACY_DRAFT_KEY = "idealStudio_unsaved_work";

const openDb = () =>
  new Promise((resolve, reject) => {
    if (typeof indexedDB === "undefined") return reject(new Error("indexedDB unavailable"));
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => req.result.createObjectStore(STORE);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });

// Runs fn(objectStore) in one transaction; resolves with the request result
// once the transaction has committed.
const withStore = async (mode, fn) => {
  const db = await openDb();
  try {
    return await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE, mode);
      const req = fn(tx.objectStore(STORE));
      tx.oncomplete = () => resolve(req ? req.result : undefined);
      tx.onerror = () => reject(tx.error);
      tx.onabort = () => reject(tx.error);
    });
  } finally {
    db.close();
  }
};

const notify = (kind) => {
  try {
    window.dispatchEvent(new CustomEvent(LOCAL_DRAFT_NOTICE_EVENT, { detail: { kind } }));
  } catch (e) {
    /* ignore */
  }
};

// Drafts are keyed per user so two users on one browser never overwrite each
// other. Pre-per-user drafts used the bare trainingId; they are adopted once
// by the user they belong to (see getDraft).
const draftKey = (trainingId, userId) => `${trainingId}:${userId}`;

// Resolves true when written. A quota failure raises the non-blocking warning.
export const putDraft = async (trainingId, draft) => {
  if (!draft.userId) return false;
  try {
    await withStore("readwrite", (s) => s.put(draft, draftKey(trainingId, draft.userId)));
    return true;
  } catch (e) {
    if (e && e.name === "QuotaExceededError") notify("quota");
    console.warn("Local draft write failed:", e);
    return false;
  }
};

const readOldKeyDraft = async (trainingId, userId) => {
  const old = await withStore("readonly", (s) => s.get(String(trainingId)));
  return old && old.userId === userId ? old : null;
};

export const getDraft = async (trainingId, userId) => {
  if (!userId) return null;
  try {
    const found = await withStore("readonly", (s) => s.get(draftKey(trainingId, userId)));
    if (found) return found;
    const old = await readOldKeyDraft(trainingId, userId);
    if (!old) return null;
    await withStore("readwrite", (s) => s.put(old, draftKey(trainingId, userId)));
    await withStore("readwrite", (s) => s.delete(String(trainingId)));
    return old;
  } catch (e) {
    return null;
  }
};

export const deleteDraft = async (trainingId, userId) => {
  if (!userId) return;
  try {
    await withStore("readwrite", (s) => s.delete(draftKey(trainingId, userId)));
    if (await readOldKeyDraft(trainingId, userId)) {
      await withStore("readwrite", (s) => s.delete(String(trainingId)));
    }
  } catch (e) {
    /* ignore */
  }
};

// Startup housekeeping: drop drafts older than DRAFT_MAX_AGE_MS.
export const purgeOldDrafts = async (now = Date.now()) => {
  try {
    await withStore("readwrite", (s) => {
      const req = s.openCursor();
      req.onsuccess = () => {
        const cursor = req.result;
        if (!cursor) return;
        const savedAt = cursor.value && cursor.value.savedAt;
        if (typeof savedAt !== "number" || now - savedAt > DRAFT_MAX_AGE_MS) cursor.delete();
        cursor.continue();
      };
      return null;
    });
  } catch (e) {
    /* ignore */
  }
};

// Redux state in the same shape the save path exports (serialized once).
const exportState = (state) =>
  JSON.stringify({
    present: {
      ...state.undoGroup.present,
      filesUploaded: state.filesUploaded,
      status: state.status,
      everPublished: state.everPublished,
    },
  });

// Cheap fingerprint of an already-serialized state (djb2-xor over the string).
const signatureOf = (json) => {
  let h = 5381;
  for (let i = 0; i < json.length; i++) h = ((h * 33) ^ json.charCodeAt(i)) >>> 0;
  return json.length + ":" + h;
};

// Signature of the state as a draft would store it (one stringify).
export const getContentSignature = (state) => signatureOf(exportState(state));

// Snapshot of the redux state: one stringify feeds both the payload and signature.
export const buildDraft = (state, userId) => {
  const exportedState = exportState(state);
  return {
    exportedState,
    userId: userId || null,
    savedAt: Date.now(),
    baseUpdatedAt: getBaseUpdatedAt(),
    signature: signatureOf(exportedState),
  };
};

let warnedNoUser = false;

export const saveDraftFromState = (trainingId, state, userId) => {
  if (trainingId == null || trainingId === "") return Promise.resolve(false);
  if (!userId) {
    // No user -> a draft could never be offered back (matched per user).
    if (!warnedNoUser) {
      warnedNoUser = true;
      console.warn("Local draft backup disabled: no user id.");
    }
    return Promise.resolve(false);
  }
  return putDraft(trainingId, buildDraft(state, userId));
};

// Pure: should the open-time modal appear, and should it carry the
// "server has a newer save" warning?
export const decideDraftPrompt = ({ draft, userId, currentSignature, serverUpdatedAt }) => {
  if (!draft || !userId || draft.userId !== userId) return { prompt: false, serverNewer: false };
  if (draft.signature === currentSignature) return { prompt: false, serverNewer: false };
  const server = Date.parse(serverUpdatedAt);
  const base = Date.parse(draft.baseUpdatedAt);
  return { prompt: true, serverNewer: !isNaN(server) && !isNaN(base) && server > base };
};

// One-time migration of the old localStorage snapshot into a draft for this
// training; removes the old key either way. Resolves the draft or null.
export const takeLegacyDraft = (trainingId, userId) => {
  try {
    const raw = localStorage.getItem(LEGACY_DRAFT_KEY);
    if (!raw) return null;
    const { state, timestamp, url } = JSON.parse(raw);
    const urlId = new URL(url).searchParams.get("id");
    // Another training's snapshot stays put until that training is opened.
    if (!state || String(urlId) !== String(trainingId)) return null;
    localStorage.removeItem(LEGACY_DRAFT_KEY);
    if (Date.now() - timestamp > DRAFT_MAX_AGE_MS) return null;
    return {
      exportedState: JSON.stringify(state),
      userId,
      savedAt: timestamp,
      baseUpdatedAt: null,
      signature: null,
    };
  } catch (e) {
    return null;
  }
};

// Debounce with a max wait: writes 5 s after the last change, but never later
// than 15 s after the first un-written change.
export const createDraftScheduler = (write, debounceMs = 5000, maxWaitMs = 15000) => {
  let timer = null;
  let firstAt = 0;
  return {
    poke() {
      const now = Date.now();
      if (!firstAt) firstAt = now;
      clearTimeout(timer);
      timer = setTimeout(
        () => {
          timer = null;
          firstAt = 0;
          write();
        },
        Math.min(debounceMs, Math.max(0, firstAt + maxWaitMs - now))
      );
    },
    cancel() {
      clearTimeout(timer);
      timer = null;
      firstAt = 0;
    },
  };
};
